import React from 'react'
import { isNil } from 'lodash'
import { IconSearch, IconSparkles } from '@tabler/icons-react'

import { AnalyticsCard } from '@/components/cards/AnalyticsCard'
import { compareToPreviousRange, formatPercent, getCardVariant } from '@/utils/helpers'

import { ANSWERED_TOOLTIP, getComparisonLabel } from './AiAnswers.helpers'

const UNAVAILABLE_VALUE = '—'
const UNAVAILABLE_CAPTION = 'Not available yet'
const ANSWERED_CAPTION = 'of questions asked'
const NO_QUESTIONS_CAPTION = 'No questions asked'

export const AiAnswersStats = ({ metrics, dateRange, metricsLoading }) => {
  // Null when the metrics request failed, as it will against an agent-gpt that
  // predates the endpoint; a dash is honest where "0 questions asked" would not be.
  const metricsAvailable = !isNil(metrics)
  const questionsAsked = metrics?.current.questions_asked
  const previousQuestionsAsked = metrics?.previous.questions_asked

  // 0 of 0 is not "0% answered" (agent-gpt sends a null rate for it), so the card
  // says there were no questions. The caption keys on the count it describes, so a
  // missing rate with questions present reads as unavailable rather than as empty.
  const noQuestions = metricsAvailable && questionsAsked === 0
  const answeredRate = metrics?.current.answered_rate
  const rateAvailable = metricsAvailable && !isNil(answeredRate)

  const getAnsweredCaption = () => {
    if (noQuestions) {
      return NO_QUESTIONS_CAPTION
    }

    if (!rateAvailable) {
      return UNAVAILABLE_CAPTION
    }

    return ANSWERED_CAPTION
  }

  return (
    <div className="analytics-card-grid small-grid" data-testid="ai-answers-stats">
      <AnalyticsCard
        icon={<IconSearch size={18} />}
        testId="stat-questions-asked"
        description="Questions asked"
        data={metricsAvailable ? questionsAsked.toLocaleString() : UNAVAILABLE_VALUE}
        percent={compareToPreviousRange(questionsAsked, previousQuestionsAsked)}
        arrowUp={questionsAsked > previousQuestionsAsked}
        variant={getCardVariant(questionsAsked, previousQuestionsAsked)}
        dateDescription={metricsAvailable ? getComparisonLabel(dateRange) : UNAVAILABLE_CAPTION}
        loading={metricsLoading}
        comparisonsLoading={metricsLoading}
      />
      <AnalyticsCard
        icon={<IconSparkles size={18} />}
        accent="magical"
        testId="stat-answered-by-agentgpt"
        description="Answered by AgentGPT"
        info={ANSWERED_TOOLTIP}
        data={rateAvailable ? formatPercent(answeredRate * 100) : UNAVAILABLE_VALUE}
        dateDescription={getAnsweredCaption()}
        loading={metricsLoading}
        comparisonsLoading={metricsLoading}
      />
    </div>
  )
}
